import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import TableUI from "../../components/TableUI";
import Pagination from "../../components/Pagination"; // ✅ custom pagination
import { ActionButton, Buttons } from "../../components/Buttons";
import { MdOutlineDelete } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import {
  fetchUsers,
  addUser,
  updateUser,
  deleteUser,
} from "../../slice/UserSlice";
import { HiOutlineDotsVertical } from "react-icons/hi";
import PageTitle from "../../components/PageTitle";
import CustomModal from "../../components/Modal";
import NotifyData from "../../components/NotifyData";
import UserCreation from "../creations/UserCreation";
import { TextInputform } from "../../components/Forms";

const User = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.users);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;

  const userSchema = [
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "user_no",
      label: "User Number",
      type: "text",
      required: true,
      disabled: true,
    },
    {
      name: "phone_number",
      label: "Mobile Number",
      type: "text",
      required: true,
    },
    {
      name: "role",
      label: "Role",
      type: "dropdown",
      required: true,
      options: [
        { value: "admin", label: "Admin" },
        { value: "superadmin", label: "Super Admin" },
      ],
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      required: true,
      classname: "form-control-padright",
    },
  ];

  const [formData, setFormData] = useState({
    name: "",
    user_no: "",
    phone_number: "",
    role: "",
    password: "",
  });
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  useEffect(() => {
    dispatch(fetchUsers(searchTerm));
  }, [dispatch, searchTerm]);

  const handleEdit = (user) => {
    setEditMode(true);
    setSelectedUser(user || {});
    setFormData({
      name: user?.name || "",
      user_no: user?.user_no || "",
      phone_number: user?.phone || "",
      role: user?.role || "",
      password: user?.password || "",
    });
    handleOpen();
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({
      name: "",
      user_no: "",
      phone_number: "",
      role: "",
      password: "",
    });
    handleOpen();
  };

  // -------------------------
  // CRUD HANDLERS (submit, update, delete)
  // -------------------------
  const handleSubmit = async () => {
    if (
      !formData.name ||
      !formData.user_no ||
      !formData.phone_number ||
      !formData.role ||
      !formData.password
    ) {
      NotifyData("User Creation Failed: All fields are required", "error");
      return;
    }
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }

    setIsSubmitting(true);

    const userData = { ...formData, current_user_id: currentUserId };
    try {
      const msg = await dispatch(addUser(userData)).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchUsers(searchTerm));
      handleClose();
      setFormData({
        name: "",
        user_no: "",
        phone_number: "",
        role: "",
        password: "",
      });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (
      !formData.name ||
      !formData.user_no ||
      !formData.phone_number ||
      !formData.role ||
      !formData.password
    ) {
      NotifyData("User Update Failed: All fields are required", "error");
      return;
    }
    if (!currentUserId || !selectedUser?.user_id) {
      NotifyData("User Update Failed: Invalid data", "error");
      return;
    }

    setIsSubmitting(true);

    const userData = {
      ...formData,
      current_user_id: currentUserId,
      user_id: selectedUser.user_id,
    };
    try {
      const msg = await dispatch(updateUser(userData)).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchUsers(searchTerm));
      handleClose();
      setFormData({
        name: "",
        user_no: "",
        phone_number: "",
        role: "",
        password: "",
      });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }
    try {
      const msg = await dispatch(
        deleteUser({ userId, currentUserId })
      ).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchUsers(searchTerm));
    } catch (error) {
      NotifyData(error.message, "error");
    }
  };

  // ✅ search + pagination
  const filteredUsers =
    users?.filter((u) =>
      u?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
    ) || [];

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ table headers
  const UserHead = ["No", "Name", "Mobile Number", "Role"];

  // ✅ table body
  const UserBody = paginatedUsers.map((user, index) => ({
    key: user.user_id,
    values: [
      (currentPage - 1) * itemsPerPage + index + 1,
      user.name,
      user.phone,
      user.role,
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => handleEdit(user),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleDelete(user.user_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col xs="6" className="py-3">
            <PageTitle PageTitle="User" showButton={false} />
          </Col>
          <Col xs="6" className="py-3 text-end">
            <Buttons
              btnlabel="Add New"
              className="add-btn"
              onClick={handleCreate}
            />
          </Col>
          <Col xs="12" lg="3" className="py-3">
            <TextInputform
              textlabel="Search User"
              PlaceHolder="Search by user name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs="12" className="d-flex justify-content-end">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </Col>
          <Col xs="12" className="py-3 tableui">
            <TableUI
              headers={UserHead}
              body={UserBody}
              showActionColumn={true}
            />
          </Col>
        </Row>
      </Container>

      <CustomModal
        show={show}
        setShow={setShow}
        pageTitle={editMode ? <>Edit User</> : <>Create User</>}
        showButton={true}
        submitButton={true}
        submitLabel={
          isSubmitting
            ? editMode
              ? "Updating..."
              : "Submitting..."
            : editMode
            ? "Update"
            : "Submit"
        }
        CancelLabel="Cancel"
        BodyComponent={
          <UserCreation
            formData={formData}
            setFormData={setFormData}
            schema={userSchema}
            editMode={editMode}
          />
        }
        OnClick={editMode ? handleUpdate : handleSubmit}
        Size="md"
        handleOpen={handleOpen}
        handleClose={handleClose}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default User;
